/* import __COLOCATED_TEMPLATE__ from './fly-power-select-autocomplete-input.hbs.js'; */
var _class;
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { schedule } from '@ember/runloop';
import { errorsArrayToString } from '@customerio/fly-components/utils/errors';
/**
  `FlyPowerSelectAutocompleteInput` wraps the `FlyPowerSelect` component, and
  passes through all the arguments that it accepts.
  `value` and `onChange` are required.

  ```hbs
  <FlyAutocompleteInput
    @value={{this.inputValue}}
    @onChange={{fn (mut this.inputValue}}
  />
  ```
  @public
  @class FlyPowerSelectAutocompleteInput
 **/
let FlyPowerSelectAutocompleteInput = (_class = class FlyPowerSelectAutocompleteInput extends Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "select", null);
    _defineProperty(this, "previousIsLoading", this.args.isLoading ?? false);
  }
  get useVerticalCollection() {
    return this.args.useVerticalCollection ?? true;
  }
  get errorType() {
    return this.args.errorType ?? 'tooltip';
  }
  get hasErrors() {
    return !isEmpty(this.args.errors);
  }
  get errorMessage() {
    if (!this.hasErrors) {
      return '';
    }
    return errorsArrayToString(this.args.errors ?? []);
  }
  isLoadingChanged() {
    let hasStoppedLoading = this.previousIsLoading && !this.args.isLoading;

    // ember-power-select doesn't watch @options to reset search results.
    // When loading stops, the implication is that options have changed, so we
    // need to force the dropdown to reset the results.
    if (hasStoppedLoading && this.select != null) {
      // We need to reset the search value first to force ember-power-select to
      // recalculate its search results. It doesn't watch changes on @options,
      // it only changes search matches if the search text changes
      this.select.actions.search('');
      this.select.actions.search(this.args.value);
    }
    this.previousIsLoading = this.args.isLoading ?? false;
  }
  onOpen(select, event) {
    if (this.args.onOpen != null) {
      this.args.onOpen(select, event);
    }

    // Protect against null/undefined, because PowerSelect internals expect
    // strings for search terms
    if (this.args.value != null) {
      select.actions.search(this.args.value);
    }
  }
  onClose(select, event) {
    if (this.args.onClose != null) {
      this.args.onClose(select, event);
    }

    // When closing, call search on PowerSelect, so that the internal searchText
    // property gets updated for the next open
    // Protect against null/undefined, because PowerSelect internals expect
    // strings for search terms
    if (this.args.value != null) {
      select.actions.search(this.args.value);
    }
  }
  onBlur(select, e) {
    // This is to prevent an edge case:
    // if the input is focused, and a click outside of it causes the whole
    // component to be destroyed (due to an if condition hiding it, for
    // example), there will be a bug complaining about setting isOpen in the
    // BasicDropdown component twice in the same loop.
    // To prevent this from happening, we'll wait for the next afterRender loop
    // and only call "close" on the dropdown if the component is still present.
    schedule('afterRender', () => {
      if (this.isDestroyed || this.isDestroying) {
        return;
      }
      if (this.args.onBlur) {
        this.args.onBlur(select, e);
      }
      select.actions.close(e);
    });
  }
  onFocus(select, e) {
    if (this.args.onFocus) {
      this.args.onFocus(select, e);
    }
    select.actions.open(e);
  }
  onOptionsMouseDown(event) {
    // This prevents the input element from prematurely losing focus when an
    // item is being clicked in the dropdown.
    // Without this, a mousedown will cause a blur on the input, which in turn
    // will trigger the onBlur action above and close the dropdown - the closing
    // would then happen before the mouseup/click event, so nothing would get
    // selected.
    event.preventDefault();
  }
  onPowerSelectChange(option) {
    let value = typeof option === 'string' ? option : option.value;
    this.args.onChange(value);
    this.args.onSelect?.(value);
  }
  onClear() {
    this.args.onClear?.();

    // Protect against null/undefined, because PowerSelect internals expect
    // strings for search terms
    if (this.args.value != null) {
      this.select?.actions.search(this.args.value);
    }
  }
  registerAPI(select) {
    this.select = select;
    if (this.args.registerAPI != null) {
      this.args.registerAPI(select);
    }
  }
}, (_applyDecoratedDescriptor(_class.prototype, "isLoadingChanged", [action], Object.getOwnPropertyDescriptor(_class.prototype, "isLoadingChanged"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onOpen", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onOpen"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onBlur", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onBlur"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onFocus", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onFocus"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onOptionsMouseDown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onOptionsMouseDown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onPowerSelectChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onPowerSelectChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onClear", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onClear"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "registerAPI", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerAPI"), _class.prototype)), _class);
export { FlyPowerSelectAutocompleteInput as default };